import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Upload, Palette, Type, Globe, CheckCircle, XCircle,
  Download, Eye, ArrowRight, ChevronDown, ChevronUp, Monitor,
  Smartphone, Sun, Moon, AlertTriangle, Info, Settings, Save,
  ExternalLink, Copy, Check
} from 'lucide-react';

type TierId = 'tier_1' | 'tier_2' | 'tier_3';

interface TierConfig {
  id: TierId;
  name: string;
  description: string;
  price: string;
  features: string[];
  limitations: string[];
}

interface BrandConfig {
  partnerId: string;
  tier: TierId;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  fontHeading: string;
  fontBody: string;
  customCss: string;
  domain: string;
  footerText: string;
  welcomeMessage: string;
}

const TIERS: TierConfig[] = [
  {
    id: 'tier_1',
    name: 'Co-Branded',
    description: 'Add your logo and accent colors while keeping core BizBuilder branding',
    price: 'Included',
    features: ['Custom logo in header', 'Accent color picker', 'Custom welcome message', 'Powered by [Partner]'],
    limitations: ['Core brand colors required', 'Fairplay Display / Raleway fonts required', 'BizBuilder logo in footer'],
  },
  {
    id: 'tier_2',
    name: 'Partner-Led',
    description: 'Full visual control with BizBuilder attribution in footer',
    price: 'R297/mo',
    features: ['Full logo replacement', 'Complete color customization', 'Custom fonts & typography', 'Custom welcome & onboarding', 'Custom email templates', 'Help center content'],
    limitations: ['"Powered by BizBuilder" in footer', 'Terms & Privacy links required', 'Partner subdomain'],
  },
  {
    id: 'tier_3',
    name: 'Full White-Label',
    description: 'Complete brand ownership with custom domain',
    price: 'R997/mo',
    features: ['Full brand replacement', 'Custom domain + SSL', 'All content customizable', 'Custom landing pages', 'Remove all BizBuilder references', 'Priority support'],
    limitations: ['Internal tracking only', 'Quarterly compliance audit', '48-hour approval SLA'],
  },
];

const FONT_OPTIONS = [
  { value: 'Fairplay Display', label: 'Fairplay Display (Default)' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Playfair Display', label: 'Playfair Display' },
];

const BODY_FONT_OPTIONS = [
  { value: 'Raleway', label: 'Raleway (Default)' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Nunito', label: 'Nunito' },
];

const DEFAULT_CONFIG: BrandConfig = {
  partnerId: '',
  tier: 'tier_1',
  logoUrl: '',
  primaryColor: '#6B5E2B',
  secondaryColor: '#2C3E50',
  fontHeading: 'Fairplay Display',
  fontBody: 'Raleway',
  customCss: '',
  domain: '',
  footerText: 'Powered by BizBuilder Platform',
  welcomeMessage: 'Welcome to your personalized business building experience.',
};

const WhiteLabelGuidelines: React.FC = () => {
  const [config, setConfig] = useState<BrandConfig>(DEFAULT_CONFIG);
  const [step, setStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  const updateConfig = (updates: Partial<BrandConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const selectedTier = TIERS.find(t => t.id === config.tier)!;

  const jsonExport = useMemo(() => JSON.stringify({
    partner_id: config.partnerId || 'partner_abc123',
    branding_tier: config.tier,
    logo_url: config.logoUrl || 'https://partner.com/logo.png',
    primary_color: config.primaryColor,
    secondary_color: config.secondaryColor,
    font_heading: config.fontHeading,
    font_body: config.fontBody,
    custom_css: config.customCss || '',
    domain: config.domain || 'app.partner.com',
    footer_text: config.footerText,
  }, null, 2), [config]);

  const cssVariables = useMemo(() => `:root {
  --partner-primary: ${config.primaryColor};
  --partner-secondary: ${config.secondaryColor};
  --partner-font-heading: '${config.fontHeading}', sans-serif;
  --partner-font-body: '${config.fontBody}', sans-serif;
  ${config.logoUrl ? `  --partner-logo-url: url('${config.logoUrl}');` : ''}
}`, [config]);

  const handleCopyJson = () => {
    navigator.clipboard.writeText(jsonExport).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleLogoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/svg+xml,image/jpeg';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          updateConfig({ logoUrl: ev.target?.result as string });
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const renderTierSelector = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-slate-800">Select White-Label Tier</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIERS.map(tier => (
          <button
            key={tier.id}
            onClick={() => updateConfig({ tier: tier.id })}
            className={`relative p-5 rounded-2xl border-2 text-left transition-all ${
              config.tier === tier.id
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <span className={`text-xs font-black uppercase tracking-wider px-2 py-1 rounded-lg ${
                config.tier === tier.id ? 'bg-blue-200 text-blue-800' : 'bg-slate-100 text-slate-500'
              }`}>
                {tier.name}
              </span>
              <span className="text-sm font-bold text-slate-700">{tier.price}</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">{tier.description}</p>
            <div className="space-y-1">
              {tier.features.map(f => (
                <div key={f} className="flex items-center gap-1.5 text-xs text-green-600">
                  <CheckCircle size={12} />
                  {f}
                </div>
              ))}
              {tier.limitations.map(l => (
                <div key={l} className="flex items-center gap-1.5 text-xs text-amber-600">
                  <AlertTriangle size={12} />
                  {l}
                </div>
              ))}
            </div>
            {config.tier === tier.id && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <Check size={12} className="text-white" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBrandingForm = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-slate-800">Branding Configuration</h3>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-2">Partner Logo</label>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
            {config.logoUrl ? (
              <img src={config.logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" />
            ) : (
              <Upload size={24} className="text-slate-300" />
            )}
          </div>
          <div>
            <button onClick={handleLogoUpload} className="px-4 py-2 bg-slate-100 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-200 transition-colors">
              Upload Logo
            </button>
            <p className="text-xs text-slate-400 mt-1">PNG, SVG, or JPG. Min 300px, transparent bg preferred.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">Primary Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={config.primaryColor}
              onChange={e => updateConfig({ primaryColor: e.target.value })}
              className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
            />
            <input
              type="text"
              value={config.primaryColor}
              onChange={e => updateConfig({ primaryColor: e.target.value })}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">Secondary Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={config.secondaryColor}
              onChange={e => updateConfig({ secondaryColor: e.target.value })}
              className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
            />
            <input
              type="text"
              value={config.secondaryColor}
              onChange={e => updateConfig({ secondaryColor: e.target.value })}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">Heading Font</label>
          <select
            value={config.fontHeading}
            onChange={e => updateConfig({ fontHeading: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white"
            disabled={config.tier === 'tier_1'}
          >
            {FONT_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">Body Font</label>
          <select
            value={config.fontBody}
            onChange={e => updateConfig({ fontBody: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white"
            disabled={config.tier === 'tier_1'}
          >
            {BODY_FONT_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {config.tier !== 'tier_1' && (
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">Custom Domain</label>
          <input
            type="text"
            value={config.domain}
            onChange={e => updateConfig({ domain: e.target.value })}
            placeholder="app.yourbrand.com"
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">SSL certificate will be provisioned automatically.</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-2">Welcome Message</label>
        <textarea
          value={config.welcomeMessage}
          onChange={e => updateConfig({ welcomeMessage: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
        />
      </div>
    </div>
  );

  const renderChecklist = () => {
    const items = [
      { label: 'Logo displays correctly (all devices)', done: !!config.logoUrl },
      { label: 'Colors applied consistently', done: true },
      { label: 'Fonts loading properly', done: true },
      { label: 'Custom domain working', done: config.tier === 'tier_1' || !!config.domain },
      { label: 'SSL certificate valid', done: config.tier === 'tier_1' || !!config.domain },
      { label: 'All links functional', done: true },
      { label: 'Mobile responsive', done: true },
      { label: 'Page speed acceptable (<3s load)', done: true },
      { label: 'Terms of Service link present', done: true },
      { label: 'Privacy Policy link present', done: true },
      { label: 'Copyright notice: © 2026 BizBuilder', done: true },
      { label: 'No prohibited content (misleading claims, competitor refs)', done: true },
    ];

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-slate-800">Pre-Launch Checklist</h3>
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              {item.done ? (
                <CheckCircle size={18} className="text-green-500 shrink-0" />
              ) : (
                <XCircle size={18} className="text-slate-300 shrink-0" />
              )}
              <span className={`text-sm ${item.done ? 'text-slate-700' : 'text-slate-400'}`}>{item.label}</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-400 text-center">
          {items.filter(i => i.done).length} / {items.length} checks passed
        </div>
      </div>
    );
  };

  const renderExport = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-slate-800">Export Configuration</h3>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-2">Branding JSON</label>
        <div className="relative">
          <pre className="bg-slate-900 text-green-400 rounded-2xl p-4 text-xs overflow-x-auto font-mono leading-relaxed max-h-60 overflow-y-auto">
            {jsonExport}
          </pre>
          <button
            onClick={handleCopyJson}
            className="absolute top-3 right-3 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-lg flex items-center gap-1.5 transition-colors"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-2">CSS Variables</label>
        <pre className="bg-slate-900 text-blue-300 rounded-2xl p-4 text-xs overflow-x-auto font-mono leading-relaxed">
          {cssVariables}
        </pre>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => {
            const blob = new Blob([jsonExport], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `branding_${config.partnerId || 'config'}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm hover:bg-blue-700 transition-colors"
        >
          <Download size={16} />
          Download JSON
        </button>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-2xl font-bold text-sm hover:bg-slate-200 transition-colors"
        >
          <Eye size={16} />
          Live Preview
        </button>
      </div>
    </div>
  );

  const renderPreview = () => (
    <AnimatePresence>
      {showPreview && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden"
        >
          <div
            className="rounded-2xl border border-slate-200 overflow-hidden mt-4"
            style={{
              '--partner-primary': config.primaryColor,
              '--partner-secondary': config.secondaryColor,
              '--partner-font-heading': config.fontHeading,
              '--partner-font-body': config.fontBody,
            } as React.CSSProperties}
          >
            <div className="p-6" style={{ fontFamily: config.fontBody }}>
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  {config.logoUrl ? (
                    <img src={config.logoUrl} alt="Logo" className="h-8" />
                  ) : (
                    <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: config.primaryColor }} />
                  )}
                  <span className="font-bold text-lg" style={{ fontFamily: config.fontHeading, color: config.secondaryColor }}>Partner Portal</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Monitor size={14} />
                  <Smartphone size={14} />
                </div>
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: config.fontHeading, color: config.primaryColor }}>
                {config.welcomeMessage}
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Your personalized business building environment is ready. Access your tools, track your progress, and grow your business.
              </p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {['Dashboard', 'Clients', 'Reports'].map(item => (
                  <div key={item} className="p-3 rounded-xl text-center text-xs font-bold" style={{ backgroundColor: config.primaryColor + '15', color: config.primaryColor }}>
                    {item}
                  </div>
                ))}
              </div>
              <div className="text-xs text-center text-slate-400 pt-4 border-t border-slate-100">
                {config.footerText}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const steps = [renderTierSelector, renderBrandingForm, renderChecklist, renderExport];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight">White-Label Guidelines</h1>
            <p className="text-sm text-slate-500 mt-1">Configure and manage partner branding</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded-lg bg-blue-100 text-blue-700 font-bold">
              {selectedTier.name}
            </span>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {['Tier Selection', 'Branding', 'Checklist', 'Export'].map((label, i) => (
            <button
              key={label}
              onClick={() => setStep(i)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                step === i ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            {steps[step]()}
          </motion.div>
        </AnimatePresence>

        {renderPreview()}

        <div className="flex justify-between">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="px-6 py-3 bg-slate-100 rounded-2xl text-sm font-bold text-slate-600 disabled:opacity-30 hover:bg-slate-200 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={() => step < steps.length - 1 ? setStep(step + 1) : setShowPreview(!showPreview)}
            className="px-6 py-3 bg-blue-600 text-white rounded-2xl text-sm font-bold hover:bg-blue-700 flex items-center gap-2 transition-colors"
          >
            {step < steps.length - 1 ? (
              <>Next <ArrowRight size={16} /></>
            ) : (
              <>{showPreview ? 'Hide Preview' : 'Show Preview'} <Eye size={16} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhiteLabelGuidelines;
